import { Check } from 'lucide-react';
import { Checkbox } from 'react-aria-components';
import { cn } from 'v5/platform/dom/cn';
import { focusRings } from './_common/focusRing';

/**
 * The round check that picks an item of a React Aria collection (`slot="selection"`), filled when
 * picked; on a picture, dark glass in a white ring. The item's text follows the label, as React
 * Aria labels a selection check by its item too.
 */
export function SelectionCheck({
  label,
  onPicture,
  locked,
  excludeFromTabOrder
}: {
  label: string;
  onPicture?: boolean;
  /** Picked for good: see `SelectionCircle`. */
  locked?: boolean;
  /** For an item that is picked from the keyboard by itself (Space): the check takes no Tab stop. */
  excludeFromTabOrder?: boolean;
}) {
  return (
    <Checkbox
      slot="selection"
      // a press picks, never opens the item; a drag from it draws the selection rectangle
      // (SelectionLayout)
      data-selection-affordance=""
      data-marquee-start=""
      aria-label={label}
      excludeFromTabOrder={excludeFromTabOrder}
      className={cn(
        'group/selection shrink-0 rounded-full',
        focusRings.focus.visible
      )}
    >
      <SelectionCircle onPicture={onPicture} locked={locked} />
    </Checkbox>
  );
}

/**
 * The selection check's look alone, for an item that is picked as a whole (a `ListBoxItem`).
 * It reads the pick off the closest `group/selection` around it, which carries `data-selected`.
 * Picked, it is filled with `--selection-picked` from around it, charcoal without one.
 */
export function SelectionCircle({
  onPicture,
  locked
}: {
  /** On a picture or a video: dark glass in a white ring, the look of the other controls there. */
  onPicture?: boolean;
  /**
   * Picked for good, not changeable: grey and ticked whatever the item says. The host keeps the
   * item from changing and its row or tile from looking picked.
   */
  locked?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-full border-2 transition-all',
        onPicture
          ? 'size-5.5 border-white shadow-[0_1px_3px_rgb(0_0_0/0.35)] backdrop-blur-xs'
          : 'size-6',
        locked && (onPicture ? 'bg-gray-400' : 'border-fg-muted bg-fg-muted'),
        !locked && [
          'group-data-selected/selection:bg-[color:var(--selection-picked,var(--color-charcoal-light))]',
          onPicture
            ? 'bg-[rgb(17_24_39/0.35)]'
            : 'border-border-default group-hover:border-charcoal-light/50 group-data-selected/selection:border-[color:var(--selection-picked,var(--color-charcoal-light))]'
        ]
      )}
    >
      <Check
        className={cn(
          'text-white transition-opacity',
          onPicture ? 'size-3.5 stroke-3' : 'size-4',
          locked
            ? 'opacity-100'
            : 'opacity-0 group-data-selected/selection:opacity-100'
        )}
      />
    </div>
  );
}
